import { Map } from 'immutable';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Media from 'react-media';
import ReactModal from 'react-modal';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import { ElementActions } from '../../actions/element';
import { validateAndDispatch } from '../../helpers/form';
import { wbsRootElementIdSelector } from '../../reducers/elements';
import { currentProjectIdSelector } from '../../reducers/projects';
import { dependencyTypeIdSelector } from '../../reducers/relationshipTypes';
import immutableCreatorSelector from '../../selectors/immutableCreatorSelector';
import InputField from '../form/InputField';
import { getComponent } from '../types/Components';

ReactModal.setAppElement('#root');

function getParent() {
  return document.querySelector('#portal-root');
}

// This modal component does not work the same as the way the FullScreenModal works.
// See QuickAddDropdownButton.js to see how to implement this react-modal based component.

const initialValuesSelector = (state, props) => {
  const modalOptions = props.options || null;
  const modalOptionsJS =
    modalOptions && Map.isMap(modalOptions)
      ? modalOptions.toJS()
      : modalOptions;
  return modalOptionsJS;
};

const rootElementIdSelector = state => state.projects.get('rootElementId');
const elementTypesSelector = state => state.elementTypes.get('elementTypes');

const CreateElementNewSelector = immutableCreatorSelector(
  [
    rootElementIdSelector,
    elementTypesSelector,
    currentProjectIdSelector,
    dependencyTypeIdSelector,
    wbsRootElementIdSelector,
    initialValuesSelector,
  ],
  (
    rootElementId,
    elementTypes,
    currentProjectId,
    relationshipTypeId,
    wbsRootElementId,
    initialValues,
  ) => ({
    rootElementId,
    elementTypes,
    currentProjectId,
    relationshipTypeId,
    wbsRootElementId,
    initialValues,
  }),
);

@connect(CreateElementNewSelector)
@reduxForm({
  form: 'createElementNew',
  destroyOnUnmount: true,
})
export default class CreateNewTasksModal extends Component {
  static propTypes = {
    contentLabel: PropTypes.string,
    showModal: PropTypes.bool,
    handleCloseModal: PropTypes.func,
    currentProjectId: PropTypes.string,
    reset: PropTypes.func,
    rootElementId: PropTypes.string,
    elementTypes: PropTypes.instanceOf(Map),
    wbsRootElementId: PropTypes.string,
    typeId: PropTypes.string,
    cb: PropTypes.func,
    handleSubmit: PropTypes.func,
    submitting: PropTypes.bool,
  };

  // This is replaced by .ReactModal__Body--open
  // See Body Class section @ https://github.com/reactjs/react-modal
  // componentWillUpdate(nextProps) {
  //   // this handles the overflow issue with the left navbar height needing overflow
  //   // when the modal is not shown. without this, you will see 2 scrollbars.
  //   if (nextProps.showModal) {
  //     document.body.style.overflowY = 'hidden';
  //   } else {
  //     document.body.style.overflowY = 'visible';
  //   }
  // }

  buildProperties = elementType => {
    const properties = elementType.get('props');

    if (!properties || !properties.size) return <span />;

    let index = 1;
    const propertiesStyle = {
      marginBottom: 10,
    };

    return (
      <div style={propertiesStyle}>
        {properties
          .sortBy(p => p.get('_order'))
          .map((prop, key) => (
            <div key={key}> {this.fieldSet(key, prop, index++)} </div>
          ))
          .toArray()}
      </div>
    );
  };

  fieldSet = (k, val, index) => {
    if (val && val.has('_type')) {
      if (val.get('_display') === 'false' || val.get('_display') === false) {
        return <div />;
      }
      const customHelpText = val.has('_helptext')
        ? val.get('_helptext')
        : false;
      const typeOptions =
        val.has('_typeOptions') && val.get('_typeOptions')
          ? val.get('_typeOptions').toJSON()
          : null;
      const defaultValue = val.get('_default');
      const TypeComponent = getComponent(val.get('_type'));
      const className =
        val.get('_type') === 'richtext'
          ? 'form-group-fix-temp row'
          : 'form-group row';

      return (
        <div
          className={className}
          style={{ marginBottom: 10 }}
          key={`${k}-${index}`}
        >
          <label
            className="col-sm-2 col-form-label"
            style={{ paddingLeft: 0, paddingRight: 10 }}
          >
            {k}
          </label>
          <div
            key={`${k}-${index}`}
            className="col-sm-10"
            style={{ padding: 0 }}
          >
            <Field
              name={k}
              key={`${k}-${index}`}
              component={TypeComponent.editor}
              defaultValue={defaultValue}
              value={null}
              uid="createElementNew"
              form="createElementNew"
              propertyKey={k}
              propTypeOptions={typeOptions}
              customHelpText={customHelpText}
              fieldIndex={index}
            />
          </div>
        </div>
      );
    }
    return null;
  };

  submit = async (values, dispatch) => {
    const {
      currentProjectId,
      reset,
      rootElementId,
      elementTypes,
      wbsRootElementId,
      typeId,
      handleCloseModal,
    } = this.props;

    const elementTypeId = typeId;
    const elementType = elementTypes.get(elementTypeId);
    const elementTypeName = elementType.get('type');

    const propKeys = [];
    const props = {};

    elementType.get('props').forEach((prop, key) => {
      // eslint-disable-line array-callback-return
      const propValue =
        values[key] || elementType.getIn(['props', key, '_default']);
      if (!propValue) return; // eslint-disable-line array-callback-return
      const compType = prop.get('_type');
      const TypeComponent = getComponent(compType);
      if (TypeComponent && TypeComponent.skipAutoSave) {
        return; // eslint-disable-line array-callback-return
      }
      propKeys.push(key);
      props[key] = propValue;
    });

    let parentId = rootElementId;
    if (elementType.get('type') === 'Work Breakdown Structure') {
      parentId = wbsRootElementId;
    }

    const createElementFormObject = {
      name: values.name || null,
      type_id: elementTypeId,
      parent_id: parentId,
      project_id: currentProjectId,
      props,
    };

    const validation = {
      name: [{ rule: 'required', label: `${elementTypeName} name` }],
    };

    const res = await validateAndDispatch(
      dispatch,
      ElementActions.createElement(currentProjectId, createElementFormObject),
      validation,
      createElementFormObject,
    );

    // Extract the ids of the elements created
    const ids = res.result.result;

    // Iterate through props and call the post submit method
    elementType.get('props').forEach(async (prop, key) => {
      // eslint-disable-line array-callback-return
      const propValue =
        values[key] || elementType.getIn(['props', key, '_default']);
      if (!propValue) return; // eslint-disable-line array-callback-return
      const compType = prop.get('_type');
      const TypeComponent = getComponent(compType);
      if (TypeComponent && TypeComponent.postCreate) {
        const typeOptions = elementType
          .getIn(['props', key, '_typeOptions'])
          .toJS();
        await TypeComponent.postCreate(
          propValue,
          dispatch,
          currentProjectId,
          ids,
          typeOptions,
        );
      }
    });

    reset();
    handleCloseModal();
  };

  render() {
    const {
      showModal,
      contentLabel,
      handleCloseModal,
      handleSubmit,
      submitting,
      elementTypes,
      typeId,
    } = this.props;

    const elementTypeId = typeId;
    const elementType = elementTypes.get(elementTypeId);
    if (!elementType) {
      return (
        <div className="fs-modal-content">
          The type is missing. Cannot load modal.
        </div>
      );
    }

    const elementTypeName = elementType.get('type');
    const elementTypeTitle =
      elementTypeName === 'Work Breakdown Structure'
        ? 'WBS Element'
        : elementTypeName;
    const disabled = submitting || false;

    console.log('SHOW MODAL', showModal);

    return (
      <Media query="(max-width: 992px)">
        {matches => (
          <ReactModal
            shouldCloseOnOverlayClick={false}
            parentSelector={getParent}
            isOpen={showModal}
            contentLabel={contentLabel}
            style={{
              overlay: {
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                overflowY: 'auto',
              },
              content: {
                marginLeft: 'auto',
                marginRight: 'auto',
                padding: 0,
                border: 'none',
                bottom: 'auto',
                borderRadius: 4,
                maxWidth: matches ? '90vw' : '60vw',
              },
            }}
          >
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#eee' }}>
                <h5 className="modal-title">Create New {elementTypeTitle}</h5>
                <button
                  onClick={handleCloseModal}
                  type="button"
                  className="close"
                  data-dismiss="modal"
                  aria-label="Close"
                >
                  <i
                    className="fa fa fa-times"
                    style={{ fontSize: '1.3em', color: '#808080' }}
                  />
                </button>
              </div>
              <div className="modal-body">
                <form>
                  <Field
                    name="name"
                    type="textarea"
                    component={InputField}
                    label="Name(s)"
                    rows="5"
                    className="form-control"
                    placeholder={`One ${elementTypeTitle} per line...`}
                    style={{ marginBottom: 10, paddingRight: 10 }}
                    wrapperClassName="form-group row"
                    labelClassName="col-sm-2 col-form-label"
                    // labelStyle={{ padding: 0 }}
                    containerClassName="col-sm-10"
                    containerStyle={{ padding: 0 }}
                    autoFocus
                  />
                  {this.buildProperties(elementType)}
                </form>
              </div>
              <div
                className="modal-footer"
                style={{ backgroundColor: '#eee', padding: 0 }}
              >
                <button
                  className="btn btn-primary modal-submit-btn"
                  onClick={handleSubmit(this.submit)}
                  disabled={disabled}
                  data-test="createButton"
                >
                  {submitting && <i className="fa fa-cog fa-spinner fa-spin" />}{' '}
                  Create New {elementTypeTitle}
                </button>
              </div>
            </div>
          </ReactModal>
        )}
      </Media>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/modals/CreateNewTasks.js